/**
 * Resolves date placeholder tokens in test scenario JSON text.
 *
 * Test data files use tokens like `__SHIP_T1__`, `__ARRIVAL_T1__`, etc.
 * instead of hardcoded dates. This ensures PO ship dates always remain
 * more than 28 days in the future (the frozen-PO threshold), regardless
 * of when the test scenario is loaded.
 *
 * Date tiers (7 levels) maintain the relative ordering the algorithm needs:
 *   T1 (earliest) → T7 (latest)
 */

function formatIsoDate(date: Date): string {
  const y = String(date.getFullYear());
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T00:00:00`;
}

function formatShortDate(date: Date): string {
  const y = String(date.getFullYear());
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(base: Date, days: number): Date {
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Ship date offsets (days from today). Each tier is well past
 * the 28-day frozen-PO threshold and maintains ordering.
 */
const SHIP_OFFSETS: Record<string, number> = {
  __SHIP_T1__: 45,
  __SHIP_T2__: 55,
  __SHIP_T3__: 60,
  __SHIP_T4__: 70,
  __SHIP_T5__: 75,
  __SHIP_T6__: 90,
  __SHIP_T7__: 95,
};

/** Arrival date = ship date + 30 days (mirrors real PO lead time). */
const ARRIVAL_OFFSETS: Record<string, number> = {
  __ARRIVAL_T1__: 75,
  __ARRIVAL_T2__: 85,
  __ARRIVAL_T3__: 90,
  __ARRIVAL_T4__: 100,
  __ARRIVAL_T5__: 105,
  __ARRIVAL_T6__: 120,
  __ARRIVAL_T7__: 125,
};

/** stockedThroughDate — always ~1 year from today. */
const STOCKED_THROUGH_OFFSET = 365;

/**
 * Replace all date tokens in test scenario JSON text with dates
 * computed relative to today.
 */
export function resolveTestDataDates(jsonText: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let result = jsonText;

  for (const [token, offset] of Object.entries(SHIP_OFFSETS)) {
    result = result.replaceAll(token, formatIsoDate(addDays(today, offset)));
  }

  for (const [token, offset] of Object.entries(ARRIVAL_OFFSETS)) {
    result = result.replaceAll(token, formatIsoDate(addDays(today, offset)));
  }

  result = result.replaceAll(
    "__STOCKED_THROUGH__",
    formatShortDate(addDays(today, STOCKED_THROUGH_OFFSET))
  );

  return result;
}
